import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import EngagementDetail from "@/pages/EngagementDetail";
import Users from "@/pages/Users";
import AuditLogs from "@/pages/AuditLogs";
import ProgramLibrary from "@/pages/ProgramLibrary";
import StandardsLibrary from "@/pages/StandardsLibrary";
import EngagementPicker from "@/pages/EngagementPicker";
import Methodology from "@/pages/Methodology";
import RiskLibrary from "@/pages/RiskLibrary";
import FirmSettings from "@/pages/FirmSettings";
import CaseStudy from "@/pages/CaseStudy";
import ComponentPortal from "@/pages/ComponentPortal";
import DeepLinkOpen from "@/pages/DeepLinkOpen";
import IntegrationHealth from "@/pages/IntegrationHealth";
import BackupRecovery from "@/pages/BackupRecovery";
import CaseWalkthrough from "@/pages/CaseWalkthrough";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Memuat...</div>;
  // Only a Word deep-link path survives sign-in; nothing else is carried as a redirect target.
  if (user === false) return <Navigate to="/login" replace state={location.pathname.startsWith("/open/") ? { from: location.pathname } : undefined} />;
  if (user.role === "Component Auditor") return <Navigate to="/portal" replace />;
  return children;
}

function PortalOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Memuat...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={user.role === "Component Auditor" ? "/portal" : "/"} replace /> : <Login />} />
      <Route path="/portal" element={<PortalOnly><ComponentPortal /></PortalOnly>} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/clients" element={<Protected><Clients /></Protected>} />
      <Route path="/open/:kind/:id" element={<Protected><DeepLinkOpen /></Protected>} />
      <Route path="/engagements/:id" element={<Protected><EngagementDetail /></Protected>} />
      <Route path="/admin/users" element={<Protected><Users /></Protected>} />
      <Route path="/admin/audit-logs" element={<Protected><AuditLogs /></Protected>} />
      <Route path="/admin/integration-health" element={<Protected><IntegrationHealth /></Protected>} />
      <Route path="/admin/backup-recovery" element={<Protected><BackupRecovery /></Protected>} />
      <Route path="/program-library" element={<Protected><ProgramLibrary /></Protected>} />
      <Route path="/standards-library" element={<Protected><StandardsLibrary /></Protected>} />
      <Route path="/methodology" element={<Protected><Methodology /></Protected>} />
      <Route path="/risk-library" element={<Protected><RiskLibrary /></Protected>} />
      <Route path="/firm/settings" element={<Protected><FirmSettings /></Protected>} />
      <Route path="/case-study" element={<Protected><CaseStudy /></Protected>} />
      <Route path="/case-study/walkthrough" element={<Protected><CaseWalkthrough /></Protected>} />
      <Route path="/m/acceptance" element={<Protected><EngagementPicker title="Penerimaan & Keberlanjutan" tabKey="acceptance" breadcrumb="Perikatan" subtitle="Pilih perikatan untuk menilai penerimaan & keberlanjutan klien" /></Protected>} />
      <Route path="/m/account-workspace" element={<Protected><EngagementPicker title="Account Working Paper Center" tabKey="accounts" subtitle="Saldo, risiko, bukti dan kesimpulan per akun" /></Protected>} />
      <Route path="/m/data-intake" element={<Protected><EngagementPicker title="Data Intake" tabKey="dataintake" breadcrumb="Perikatan" subtitle="Unggah & rekonsiliasi data sumber per perikatan" /></Protected>} />
      <Route path="/m/fraud-jet" element={<Protected><EngagementPicker title="Fraud & JET Center" tabKey="fraud" breadcrumb="Perikatan" subtitle="Pusat kecurangan & pengujian jurnal per perikatan" /></Protected>} />
      <Route path="/m/sampling" element={<Protected><EngagementPicker title="Sampling Center" tabKey="sampling" breadcrumb="Perikatan" subtitle="Perencanaan & evaluasi sampel per perikatan" /></Protected>} />
      <Route path="/m/confirmations" element={<Protected><EngagementPicker title="Konfirmasi" tabKey="confirmations" breadcrumb="Perikatan" subtitle="Pusat konfirmasi eksternal per perikatan" /></Protected>} />
      <Route path="/m/smm" element={<Protected><EngagementPicker title="SMM Dashboard" tabKey="quality" breadcrumb="Manajemen Kualitas" subtitle="Sistem Manajemen Mutu & Grup per perikatan" /></Protected>} />
      <Route path="/m/eqr" element={<Protected><EngagementPicker title="EQR" tabKey="quality" breadcrumb="Manajemen Kualitas" subtitle="Reviu Pengendalian Mutu Perikatan (EQR)" /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
